import PageHeader from 'components/general/pageHeader';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './welcome.css';
import Course from 'components/prism/course';
import ToDo from './components/todo';
import PathwayCard from '../../components/prism/pathwayCard';
import Leaderboard from './components/leaderboard';
import Metrics from '../../components/prism/metrics';
import Recommended from './components/recommended';
import { apiGet } from 'services';
import { Loading, SystemText, DebugInfo } from 'e4shared';
import { setPrismMenu } from 'actions/ui';
import Subheader from 'components/prism/subheader';
import { isFuture } from 'helpers/dates';

export const Welcome = ({ match }) => {
    const dispatch = useDispatch();
    const [period, setPeriod] = React.useState('');
    const [boardPeriod, setBoardPeriod] = React.useState('M');
    const [profileId, setProfileId] = React.useState(null);
    const [profile, setProfile] = React.useState(null);
    const [data, setData] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [loadingBoard, setLoadingBoard] = React.useState(false);
    const [loadingMetrics, setLoadingMetrics] = React.useState(true);
    const [isMe, setIsMe] = React.useState(true);
    const { user } = useSelector(state => state.user);
    const { prismMultiColumn } = useSelector(state => state.ui);

    React.useEffect(() => {
        dispatch(setPrismMenu('Home'));
    }, []);

    const showLeaderboard = React.useMemo(() => {
        return data?.leaderboard?.length > 0; // TODO - Additionally check from settings once card done
    }, [data?.leaderboard, user]);

    const showMetrics = React.useMemo(() => {
        return data?.metrics?.length > 0; // TODO - Additionally check from settings once card done
    }, [data?.metrics, user]);

    React.useEffect(() => {
        DebugInfo('component', 'Welcome');
    }, []);

    React.useEffect(() => {
        const profileId = match.params.id ? parseInt(match.params.id, 10) : user.id;
        setIsMe(profileId === user.id);
        setProfileId(profileId || user.id);
    }, [match.params.id, user.id]);

    React.useEffect(() => {
        if (profileId) {
            apiGet(`profile/${profileId}`, user.guid, response => {
                setProfile(response);
            });

            apiGet(`welcome/${profileId}?period=${period}`, user.guid, response => {
                // Filter out reviews with launch dates in the future
                const filteredReviews = (response.reviews || []).filter(item => !item.launch || !isFuture(item.launch));
                const allToDo = [...(response.toDo || []), ...filteredReviews].sort((a, b) => (a.due > b.due ? 1 : -1));

                // Filter out pathway if launch date is in the future
                const pathway =
                    response.pathway && response.pathway.launch && isFuture(response.pathway.launch)
                        ? null
                        : response.pathway;

                setData({
                    ...response,
                    pathway,
                    toDo: allToDo,
                });

                setLoading(false);
                setPeriod('M');
            });
        }
    }, [profileId]);

    React.useEffect(() => {
        if (!loading && profileId && period) {
            setLoadingMetrics(true);
            apiGet(`welcome/${profileId}?period=${period}&metrics=true`, user.guid, response => {
                setData(prev => ({ ...prev, metrics: response.metrics }));
                setLoadingMetrics(false);
            });
        }
    }, [period, loading, profileId]);

    React.useEffect(() => {
        if (!loading && profileId) {
            setLoadingBoard(true);
            apiGet(`welcome/${profileId}?boardperiod=${boardPeriod}&board=1`, user.guid, response => {
                setData(prev => ({ ...prev, leaderboard: response.leaderboard }));
                setLoadingBoard(false);
            });
        }
    }, [boardPeriod]);

    const text = React.useMemo(
        () => ({
            title: SystemText('Welcome/Title', user?.first_Name),
            titleOther: SystemText('Welcome/Title/Other', profile?.first_Name),
            pathway: {
                title: SystemText('Welcome/Pathway/Title'),
            },
            pickup: {
                title: SystemText('Welcome/Pickup/Title'),
            },
            toDo: {
                title: SystemText('Welcome/ToDo/Title'),
                reviewTitle: SystemText('Welcome/ToDo/ReviewTitle'),
                reviewSummary: SystemText('Welcome/ToDo/ReviewSummary'),
                noResults: SystemText('Welcome/ToDo/NoResults/Title'),
                noResultsPathway: SystemText('Welcome/ToDo/NoResults/TitleWithPathway'),
                noResultsSummary: SystemText('Welcome/ToDo/NoResults/Summary'),
                noResultsSummaryPathway: SystemText('Welcome/ToDo/NoResults/SummaryWithPathway'),
                noResultsSummaryBehaviours: SystemText('Welcome/ToDo/NoResults/SummaryWithBehaviours'),
            },
            recommended: {
                title: SystemText('Welcome/Recommended/Title'),
                summary: SystemText('Welcome/Recommended/Summary'),
                browseAll: SystemText('Welcome/Recommended/BrowseAll'),
            },
            metrics: {
                title: SystemText('Welcome/Metrics/Title'),
            },
            leaderboard: {
                title: SystemText('Welcome/Leaderboard/Title'),
                month: SystemText('Welcome/Leaderboard/MTD'),
                quarter: SystemText('Welcome/Leaderboard/QTD'),
                year: SystemText('Welcome/Leaderboard/YTD'),
                allTime: SystemText('Welcome/Leaderboard/All'),
            },
        }),
        [profile]
    );

    const displayPickup = !data.pathway?.id && data.pickup;

    return (
        <div id="welcome-page" data-testid="welcome-page">
            <PageHeader title={isMe ? text.title : text.titleOther} homepage />
            <section className="content mtm10">
                {loading ? (
                    <Loading />
                ) : (
                    <div>
                        {data.pathway && (
                            <>
                                <Subheader title={text.pathway.title} />
                                <PathwayCard text={text.pathway} pathway={data.pathway} />
                            </>
                        )}
                        <div className={`mt30 ${displayPickup && prismMultiColumn ? 'two-col-66' : ''}`}>
                            <div>
                                <ToDo
                                    text={text.toDo}
                                    profileId={profileId}
                                    data={data.toDo}
                                    hasPathway={!!data.pathway}
                                    hasBehaviours={data.recommended?.length > 0}
                                />
                            </div>
                            {displayPickup && (
                                <div>
                                    <Subheader title={text.pickup.title} h2class={!prismMultiColumn ? 'mt20' : ''} />
                                    <Course course={data.pickup} />
                                </div>
                            )}
                        </div>
                        {data.recommended?.length > 0 && !data.pathway?.disable_Recommendations && (
                            <div className="mt30">
                                <Recommended text={text.recommended} data={data.recommended} />
                            </div>
                        )}
                        {showMetrics && showLeaderboard && (
                            <div className={`${prismMultiColumn ? 'two-col mt30' : ''}`}>
                                <div className={`${!prismMultiColumn ? 'mt30' : ''}`}>
                                    <Metrics
                                        title={text.metrics.title}
                                        data={data.metrics}
                                        period={period}
                                        setPeriod={setPeriod}
                                        loading={loadingMetrics}
                                    />
                                </div>
                                <div className={`${!prismMultiColumn ? 'mt30' : ''}`}>
                                    <Leaderboard
                                        text={text.leaderboard}
                                        data={data.leaderboard}
                                        period={boardPeriod}
                                        setPeriod={setBoardPeriod}
                                        loading={loadingBoard}
                                    />
                                </div>
                            </div>
                        )}
                        {showMetrics && !showLeaderboard && (
                            <div className="mt30">
                                <Metrics
                                    text={text.metrics}
                                    data={data.metrics}
                                    period={period}
                                    setPeriod={setPeriod}
                                    loading={loadingMetrics}
                                />
                            </div>
                        )}
                        {!showMetrics && showLeaderboard && (
                            <div className="mt30">
                                <Leaderboard
                                    text={text.leaderboard}
                                    data={data.leaderboard}
                                    period={boardPeriod}
                                    setPeriod={setBoardPeriod}
                                    loading={loadingBoard}
                                />
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
};

export default Welcome;
